import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@shared/api";
import { Banner, Empty, Field, Loading, formatDate } from "@shared/ui";
import { LOCALE_OPTIONS, TIMEZONE_OPTIONS, type Workspace } from "@shared/types";

export function WorkspacesPage() {
  const [workspaces, setWorkspaces] = useState<Workspace[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  async function load() {
    try {
      const data = await api.get<{ workspaces: Workspace[] }>("/api/admin/workspaces");
      setWorkspaces(data.workspaces);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not load clients");
    }
  }

  useEffect(() => {
    void load();
  }, []);

  return (
    <>
      <div className="page-head">
        <div>
          <h1>Clients</h1>
          <p className="page-sub">
            One workspace per client. Each has its own brand voice, channels, AI keys and review
            plans.
          </p>
        </div>
        <button className="primary" onClick={() => setCreating((v) => !v)}>
          {creating ? "Cancel" : "Add client"}
        </button>
      </div>

      {error ? <Banner kind="error">{error}</Banner> : null}

      {creating ? (
        <CreateWorkspaceCard
          onDone={() => {
            setCreating(false);
            void load();
          }}
        />
      ) : null}

      {workspaces === null ? (
        <Loading label="Loading clients" />
      ) : workspaces.length === 0 ? (
        <Empty title="No clients yet">
          Add your own business as the first workspace, then add the clients you manage.
        </Empty>
      ) : (
        <div className="grid grid-2">
          {workspaces.map((ws) => (
            <Link
              key={ws.id}
              to={`/workspaces/${ws.id}`}
              className="card"
              style={{ textDecoration: "none", display: "block" }}
            >
              <div className="card-head">
                <h2>{ws.name}</h2>
                <span className="pill">{ws.defaultLocale}</span>
              </div>
              <p className="muted" style={{ marginTop: 6 }}>
                {ws.timezone} · added {formatDate(ws.createdAt)}
              </p>
              {ws.brandVoice ? (
                <p className="muted" style={{ marginTop: 10 }}>
                  {ws.brandVoice.slice(0, 110)}
                  {ws.brandVoice.length > 110 ? "…" : ""}
                </p>
              ) : (
                <p className="muted" style={{ marginTop: 10 }}>
                  No brand voice set yet.
                </p>
              )}
            </Link>
          ))}
        </div>
      )}
    </>
  );
}

function CreateWorkspaceCard({ onDone }: { onDone: () => void }) {
  const [name, setName] = useState("");
  const [timezone, setTimezone] = useState("Europe/Oslo");
  const [defaultLocale, setDefaultLocale] = useState("en");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function submit(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      await api.post("/api/admin/workspaces", { name, timezone, defaultLocale });
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Could not create the client");
    } finally {
      setBusy(false);
    }
  }

  return (
    <form className="card" onSubmit={submit} style={{ marginBottom: 16 }}>
      <h2 style={{ marginBottom: 14 }}>New client</h2>
      {error ? <Banner kind="error">{error}</Banner> : null}
      <Field label="Client name">
        <input value={name} onChange={(e) => setName(e.target.value)} required autoFocus />
      </Field>
      <div className="row">
        <Field label="Timezone" hint="Used to interpret and display posting times.">
          <select value={timezone} onChange={(e) => setTimezone(e.target.value)}>
            {TIMEZONE_OPTIONS.map((tz) => (
              <option key={tz} value={tz}>
                {tz}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Default language" hint="The language posts are written in by default.">
          <select value={defaultLocale} onChange={(e) => setDefaultLocale(e.target.value)}>
            {LOCALE_OPTIONS.map((l) => (
              <option key={l.value} value={l.value}>
                {l.label}
              </option>
            ))}
          </select>
        </Field>
      </div>
      <div className="actions">
        <button type="submit" className="primary" disabled={busy || !name.trim()}>
          {busy ? "Creating…" : "Create client"}
        </button>
      </div>
    </form>
  );
}
